import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { Variant } from 'shared/types/product';
import Button from 'components/commercetools-ui/atoms/button';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import ProductInformation from 'components/commercetools-ui/organisms/product/product-details/components/product-information';
import useTrack from 'components/commercetools-ui/organisms/product/product-details/hooks/useTrack';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { PDP_PRODUCT_ADDED_TO_CART } from 'helpers/constants/events';
import { useFormat } from 'helpers/hooks/useFormat';
import useGetUserInfo from 'helpers/hooks/useGetUserInfo';
import usePath from 'helpers/hooks/usePath';
import { getTranslationLanguages } from 'project.config';
import { ProjectConfig } from 'types/project-config';
import { useAccount, useCart } from 'frontastic';

interface SelectOption {
  label: string;
  value: string;
}

export interface ProductDetailsCertificationCardsProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  projectConfig: ProjectConfig;
}

interface PadiCertification {
  cardGroupCode: string;
  cardStockImageUrl: string;
  certificationCode: string;
  certificationDate: string;
  certificationId: number;
  certificationNumber: string;
  credentialName: string;
  name: string;
}

const ProductDetailsCertificationCards: FC<ProductDetailsCertificationCardsProps> = ({
  product,
  variant,
  onChangeVariant,
  onAddToCart,
}) => {
  const { addItem } = useCart();
  const { formatMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { idToken, checkUserActiveClubMember } = useGetUserInfo();
  const { loggedIn, getPublicConfig } = useAccount();
  const [quantity, setQuantity] = useState<number>(1);
  const [added, setAdded] = useState(false);
  const { path } = usePath();
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const { trackAddToCart } = useTrack({ product });
  const [isLoading, setIsLoading] = useState<boolean[]>([]);
  const [formSelectedCertificateId, setFormSelectedCertificateId] = useState<string>('');
  const [formSelectedCertificateName, setFormSelectedCertificateName] = useState<string>('');
  const [selectCertOptions, setSelectCertOptions] = useState<SelectOption[]>([]);
  const [ssoAccountDomain, setSsoAccountDomain] = useState<{ setting: string }>({ setting: '' });
  const [clientId, setClientId] = useState<{ setting: string }>({ setting: '' });
  const [isClubMember, setIsClubMember] = useState(true); // set default to true to prevent the message flash.

  const { show } = useAddToCartOverlay();

  const isFreeECard = useMemo(() => {
    return variant?.attributes?.promotion === 'clubFreeEcard';
  }, [variant]);

  const canBuyFreeECard = useMemo(() => {
    return isFreeECard ? isClubMember : true;
  }, [isFreeECard, isClubMember]);

  const getSetUserClubEntitlements = useCallback(async () => {
    setIsLoading((prev) => [...prev, true]);

    const isActiveClubMember = await checkUserActiveClubMember();
    setIsClubMember(isActiveClubMember);
    setIsLoading((prev) => prev.slice(1));
  }, [checkUserActiveClubMember]);

  useEffect(() => {
    if (idToken) {
      getSetUserClubEntitlements();
    }
  }, [idToken, getSetUserClubEntitlements]);
  const excerpt = useMemo(() => {
    const field = product?.variants?.[0]?.attributes?.['card-excerpt'];
    return typeof field === 'string' ? field : '';
  }, [product]);

  const cartGroups = useMemo(
    () => variant?.attributes?.['card-groups']?.map((group: { key: string }) => group.key),
    [variant],
  );
  const handleAddToCart = async () => {
    setIsLoading((prev) => [...prev, true]);

    const custom = {
      type: {
        key: 'certification',
        typeId: 'type',
      },

      fields: {
        certificationId: formSelectedCertificateId,
        certificationName: formSelectedCertificateName,
      },
    };

    addItem(product, variant, quantity, custom).then(() => {
      setIsLoading((prev) => prev.slice(1));
      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);
      show(product, variant, quantity);
      onAddToCart?.();
    });

    trackAddToCart(quantity);
  };

  const onLoginClick = async () => {
    if (!ssoAccountDomain.setting || !clientId.setting) return;
    setIsLoading((prev) => [...prev, true]);
    const urlLocaleParts = locale.split('-');
    const twoDigitCodeLanguage = urlLocaleParts[0];
    const languages = getTranslationLanguages();
    const language = languages[twoDigitCodeLanguage]?.languageCode;
    const query = searchParams.toString();
    let lvp = `${path}?${query}`;
    if (path && path.startsWith('/')) {
      lvp = lvp.substring(1);
    }
    const ssoLink = `${ssoAccountDomain.setting}/login/sign-in/${language}?client_id=${clientId.setting}&redirect_uri=${window.location.origin}/${locale}/padi-sso/callback&extraData=lvp=${lvp}`;
    window.location.href = ssoLink;
  };
  // Populate dropdown with users certificates.
  const updateCertificationOptions = useCallback(
    async (certifications: PadiCertification[]) => {
      if (!certifications) return;
      const certOptions: SelectOption[] = [];
      const nonSellableCerts = [-1, 0, 5, 11, 13, 14, 22, 58, 99, 100, 3391, 4500];
      certifications.forEach((certification) => {
        const certificationId = certification.certificationId;
        const certificationName = certification.credentialName;
        if (!nonSellableCerts.includes(certificationId) && cartGroups?.includes(certification.cardGroupCode)) {
          certOptions.push({
            label: certificationName,
            value: `${certificationId}`,
          });
        }
      });
      if (certOptions.length > 0) {
        setFormSelectedCertificateId(certOptions[0].value);
        setFormSelectedCertificateName(certOptions[0].label);
      }

      setSelectCertOptions(certOptions);
    },
    [cartGroups],
  );
  const [userCertifications, setUserCertifications] = useState<PadiCertification[] | undefined>(undefined);

  const hasNoCertifications = useMemo(
    () => !userCertifications || userCertifications.length === 0,
    [userCertifications],
  );

  // Get users certificates.
  const getUserCertifications = useCallback(
    async (idToken: string) => {
      setIsLoading((prev) => [...prev, true]);
      const cfg = await getPublicConfig();
      if (!cfg.proApiDomain) return;
      const getCerts = await fetch(`${cfg.proApiDomain}/certifications/me`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });
      const certs = await getCerts.json();
      setUserCertifications(certs.items);
      if (certs?.items) {
        updateCertificationOptions(certs.items);
      }

      setIsLoading((prev) => prev.slice(1));
    },
    [getPublicConfig, updateCertificationOptions],
  );

  const handleCertificationChange = async (e: React.FormEvent) => {
    const newValue = (e.target as HTMLSelectElement).value;

    setFormSelectedCertificateId(newValue.toString());
    userCertifications?.some((cert) => {
      if (cert.certificationId.toString() == newValue) {
        setFormSelectedCertificateName(cert.credentialName);
      }
    });
  };

  useEffect(() => {
    if (idToken) {
      getUserCertifications(idToken);
    }
  }, [idToken, getUserCertifications]);

  useEffect(() => {
    const fetchSsoAccountDomain = async () => {
      const cfg = await getPublicConfig();
      setSsoAccountDomain({ setting: cfg.ssoAccountDomain });
    };
    fetchSsoAccountDomain();
  }, [getPublicConfig]);

  useEffect(() => {
    const fetchClientId = async () => {
      const cfg = await getPublicConfig();
      setClientId({ setting: cfg.cognitoClientId });
    };
    fetchClientId();
  }, [getPublicConfig]);

  return (
    <div
      data-id="certification-cards-container"
      className="mx-auto max-w-7xl px-24 pb-32 pt-20 md:grid md:grid-cols-12 md:items-start md:pt-24 lg:px-32"
    >
      <div data-id="gallery-section" className="md:col-span-7 md:pr-26 lg:col-span-8 lg:pr-60">
        <Gallery
          images={variant?.images ?? []}
          imagesExtra={variant?.imagesExtra ?? []}
          firstImagePriority={true}
          hideArrowsOnSingleSlide={true}
        />
      </div>

      <div data-id="product-info-section" className="mt-24 md:col-span-5 md:mt-0 lg:col-span-4">
        <ProductInformation product={product} variant={variant} onChangeVariant={onChangeVariant} />

        {!loggedIn ? (
          <div data-id="logged-out-section" className="flex flex-col gap-32 pt-20">
            <div data-id="login-prompt">
              <a
                data-id="login-link"
                className="cursor-pointer text-padi-blue hover:text-padi-blue-darker"
                onClick={onLoginClick}
              >
                {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
              </a>{' '}
              {formatMessage({ id: 'to_purchase_card', defaultMessage: 'to purchase a replacement card.' })}
            </div>
            <Button
              data-id="sign-in-button"
              className="w-full rounded-sm border-[#D8D8DA] text-14 font-medium"
              variant="secondary"
              onClick={onLoginClick}
              dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
              loading={isLoading.length > 0}
            >
              {formatAccountMessage({ id: 'sign.in', defaultMessage: 'Sign in' })}
            </Button>
            <p data-id="certification-lookup-text">
              {formatMessage({
                id: 'are_you_padi_certified',
                defaultMessage: "Are you PADI certified but don't have a PADI account yet?",
              })}
              <a
                data-id="find-certifications-link"
                className="capitalize text-padi-blue hover:text-padi-blue-darker"
                href={`${window?.location?.origin}/login/?loginPath=lookup&lvp=certification-cards`}
              >
                {' '}
                {formatMessage({ id: 'find_your_certifications', defaultMessage: 'Find your certifications.' })}
              </a>
            </p>
          </div>
        ) : (
          <>
            <div data-id="logged-in-section" className="flex flex-col gap-8 py-20">
              <div data-id="certification-selector">
                <label data-id="certification-label" className="font-medium" tabIndex={0}>
                  {formatMessage({ id: 'select_cert', defaultMessage: 'Select Certification' })}
                </label>
                <Dropdown
                  data-id="certification-dropdown"
                  className="h-full rounded-sm"
                  value={formSelectedCertificateId.toString()}
                  items={selectCertOptions}
                  onChange={handleCertificationChange}
                  disabled={isLoading.length > 0}
                />
              </div>
              {isLoading.length === 0 && (
                <>
                  {selectCertOptions.length === 0 && canBuyFreeECard && (
                    <div data-id="no-certifications-warning" className="flex flex-row gap-8">
                      <InformationCircleIcon data-id="warning-icon" className="size-24 min-w-24 text-red-500" />
                      <div data-id="warning-content">
                        <p data-id="no-certs-message" className="flex-1 text-sm text-red-500">
                          {hasNoCertifications
                            ? formatMessage({
                                id: 'no_certs',
                                defaultMessage:
                                  'We didn’t find any PADI certifications associated with your PADI account.',
                              })
                            : formatMessage({
                                id: 'no_eligible_certs',
                                defaultMessage:
                                  'We didn’t find any PADI certifications that can be used for this card design associated with your PADI account.',
                              })}{' '}
                          <a
                            data-id="lookup-certifications-link"
                            href={`${ssoAccountDomain.setting}/login/lookup`}
                            className="underline"
                          >
                            {formatMessage({
                              id: 'find_your_certifications',
                              defaultMessage: 'Find your certifications.',
                            })}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                  {!canBuyFreeECard && (
                    <div data-id="club-member-warning" className="flex flex-row items-center gap-8">
                      <InformationCircleIcon data-id="club-warning-icon" className="size-24 text-red-500" />
                      <p data-id="club-member-message" className="flex-1 text-sm text-red-500">
                        {formatMessage({
                          id: 'club_member_only',
                          defaultMessage: 'This card is available exclusively to PADI Club members.',
                        })}{' '}
                        <a
                          data-id="purchase-club-link"
                          href={`/${locale}/subscription/padi-club-membership/p/MPC40955-1B2C/`}
                          className="underline"
                        >
                          {formatMessage({
                            id: 'purchase_club_member_link',
                            defaultMessage: ' Click here to purchase a PADI Club membership',
                          })}
                        </a>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {excerpt && (
              <div
                data-id="card-excerpt"
                className="mb-20 text-sm text-slate-700"
                dangerouslySetInnerHTML={{ __html: excerpt }}
              />
            )}

            <Button
              data-id="add-to-cart-button"
              className="w-full rounded-sm text-14 font-medium"
              variant="primary"
              onClick={handleAddToCart}
              loading={isLoading.length > 0}
              added={added}
              dataAnalytics={PDP_PRODUCT_ADDED_TO_CART}
              disabled={!formSelectedCertificateId || !canBuyFreeECard}
            >
              {formatMessage({ id: 'cart.add', defaultMessage: 'Add to cart' })}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(ProductDetailsCertificationCards);
